import { useEffect, useMemo, useState } from 'react';

import MenuBar from '../../USA/Component/MenuBar';
import Footer from '../../USA/Component/Footer';

import { getCountries, getStatesByCountry } from '../services/commonChannel';
import { createAuthNetRecurringPayment } from '../services/paymentChannel';
import { calculateMonthlyAmount } from '../utils/priceCalculator';
import { validateEmail, validatePhone } from '../utils/validators';

const emptyAttendee = () => ({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  relation: '',
  address: '',
});

function validateMember(member) {
  const errors = {};

  if (!member.firstName?.trim()) errors.firstName = 'First name is required';
  if (!member.lastName?.trim()) errors.lastName = 'Last name is required';

  if (!member.email?.trim()) errors.email = 'Email is required';
  else if (!validateEmail(member.email)) errors.email = 'Enter a valid email';

  if (!member.phone?.trim()) errors.phone = 'Phone is required';
  else if (!validatePhone(member.phone)) errors.phone = 'Enter a valid phone number';

  if (!member.address?.trim()) errors.address = 'Address is required';

  if (!member.countryId) errors.countryId = 'Country is required';
  if (!member.stateId) errors.stateId = 'State is required';

  return errors;
}

function validateAttendee(att) {
  const errors = {};

  if (!att.firstName?.trim()) errors.firstName = 'First name is required';
  if (!att.lastName?.trim()) errors.lastName = 'Last name is required';

  if (!att.email?.trim()) errors.email = 'Email is required';
  else if (!validateEmail(att.email)) errors.email = 'Enter a valid email';

  if (!att.phone?.trim()) errors.phone = 'Phone is required';
  else if (!validatePhone(att.phone)) errors.phone = 'Enter a valid phone number';

  if (!att.relation?.trim()) errors.relation = 'Relation is required';
  if (!att.address?.trim()) errors.address = 'Address is required';

  return errors;
}

export default function MembershipRegister() {
  const [member, setMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    countryId: '',
    stateId: '',
  });

  const [attendees, setAttendees] = useState([]);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  const totalMembers = 1 + attendees.length;
  const amount = useMemo(() => calculateMonthlyAmount(totalMembers), [totalMembers]);

  const memberErrors = useMemo(() => validateMember(member), [member]);
  const attendeeErrors = useMemo(
    () => attendees.map((a) => validateAttendee(a)),
    [attendees]
  );

  const isValid = useMemo(() => {
    const hasMemberErrors = Object.keys(memberErrors).length > 0;
    const hasAttendeeErrors = attendeeErrors.some((e) => Object.keys(e).length > 0);
    return !hasMemberErrors && !hasAttendeeErrors;
  }, [memberErrors, attendeeErrors]);

  useEffect(() => {
    let mounted = true;
    setLoadingCountries(true);
    getCountries()
      .then((list) => {
        if (!mounted) return;
        setCountries(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (!mounted) return;
        setFormError(e?.message || 'Failed to load countries');
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingCountries(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!member.countryId) {
      setStates([]);
      setMember((m) => ({ ...m, stateId: '' }));
      return;
    }

    let mounted = true;
    setLoadingStates(true);
    getStatesByCountry({ countryId: member.countryId })
      .then((list) => {
        if (!mounted) return;
        setStates(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (!mounted) return;
        setFormError(e?.message || 'Failed to load states');
      })
      .finally(() => {
        if (!mounted) return;
        setLoadingStates(false);
      });

    return () => {
      mounted = false;
    };
  }, [member.countryId]);

  const onAddAttendee = () => {
    setAttendees((prev) => [...prev, emptyAttendee()]);
  };

  const onRemoveAttendee = (idx) => {
    setAttendees((prev) => prev.filter((_, i) => i !== idx));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    if (!isValid) {
      setFormError('Please fix the validation errors.');
      return;
    }

    setSubmitting(true);
    try {
      const paymentPayload = {
        amount,
        currency: 'USD',
        member: {
          firstName: member.firstName,
          lastName: member.lastName,
          email: member.email,
          phone: member.phone,
        },
      };

      await createAuthNetRecurringPayment(paymentPayload);

      setFormSuccess('Payment request submitted successfully.');
    } catch (err) {
      setFormError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MenuBar />

      <section className="page-banner">
        <div className="image-layer"></div>

        <div className="auto-container">
          <h1>Membership Registration</h1>
        </div>

        <div className="breadcrumb-box">
          <div className="auto-container">
            <ul className="bread-crumb clearfix">
              <li>
                <a href="/USA/Home">Home</a>
              </li>
              <li className="active">Membership Registration</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="contact-section contact-page" style={{ marginTop: '20px' }}>
        <div className="auto-container">
          <div className="sec-title centered">
            <div className="upper-icon"></div>
            <h2>Register</h2>
          </div>

          <div className="row clearfix">
            <div className="form-column col-xl-8 col-lg-12 col-md-12 col-sm-12">
              <div className="inner">
                <div className="default-form contact-form">
                  <form onSubmit={onSubmit}>
                    {formError ? (
                      <div className="alert alert-danger" role="alert">
                        {formError}
                      </div>
                    ) : null}

                    {formSuccess ? (
                      <div className="alert alert-success" role="alert">
                        {formSuccess}
                      </div>
                    ) : null}

                    <div className="row clearfix">
                      <div className="col-md-6 col-sm-12 form-group">
                        <div className="field-label">First Name</div>
                        <input
                          type="text"
                          value={member.firstName}
                          onChange={(e) => setMember((m) => ({ ...m, firstName: e.target.value }))}
                        />
                        {memberErrors.firstName ? (
                          <div className="text-danger">{memberErrors.firstName}</div>
                        ) : null}
                      </div>

                      <div className="col-md-6 col-sm-12 form-group">
                        <div className="field-label">Last Name</div>
                        <input
                          type="text"
                          value={member.lastName}
                          onChange={(e) => setMember((m) => ({ ...m, lastName: e.target.value }))}
                        />
                        {memberErrors.lastName ? (
                          <div className="text-danger">{memberErrors.lastName}</div>
                        ) : null}
                      </div>

                      <div className="col-md-6 col-sm-12 form-group">
                        <div className="field-label">Email</div>
                        <input
                          type="email"
                          value={member.email}
                          onChange={(e) => setMember((m) => ({ ...m, email: e.target.value }))}
                        />
                        {memberErrors.email ? (
                          <div className="text-danger">{memberErrors.email}</div>
                        ) : null}
                      </div>

                      <div className="col-md-6 col-sm-12 form-group">
                        <div className="field-label">Phone</div>
                        <input
                          type="text"
                          value={member.phone}
                          onChange={(e) => setMember((m) => ({ ...m, phone: e.target.value }))}
                        />
                        {memberErrors.phone ? (
                          <div className="text-danger">{memberErrors.phone}</div>
                        ) : null}
                      </div>

                      <div className="col-md-12 col-sm-12 form-group">
                        <div className="field-label">Address</div>
                        <input
                          type="text"
                          value={member.address}
                          onChange={(e) => setMember((m) => ({ ...m, address: e.target.value }))}
                        />
                        {memberErrors.address ? (
                          <div className="text-danger">{memberErrors.address}</div>
                        ) : null}
                      </div>

                      <div className="col-md-6 col-sm-12 form-group">
                        <div className="field-label">Country</div>
                        <select
                          className="form-select"
                          value={member.countryId}
                          onChange={(e) =>
                            setMember((m) => ({
                              ...m,
                              countryId: e.target.value,
                            }))
                          }
                          disabled={loadingCountries}
                        >
                          <option value="">{loadingCountries ? 'Loading...' : 'Select Country'}</option>
                          {countries.map((c) => (
                            <option
                              key={`${String(c?.id ?? c?.name ?? '')}-${String(countries.indexOf(c))}`}
                              value={String(c?.id ?? c?.name ?? '')}
                            >
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {memberErrors.countryId ? (
                          <div className="text-danger">{memberErrors.countryId}</div>
                        ) : null}
                      </div>

                      <div className="col-md-6 col-sm-12 form-group">
                        <div className="field-label">State</div>
                        <select
                          className="form-select"
                          value={member.stateId}
                          onChange={(e) => setMember((m) => ({ ...m, stateId: e.target.value }))}
                          disabled={!member.countryId || loadingStates}
                        >
                          <option value="">
                            {!member.countryId
                              ? 'Select Country first'
                              : loadingStates
                              ? 'Loading...'
                              : 'Select State'}
                          </option>
                          {states.map((s) => (
                            <option
                              key={`${String(s?.id ?? s?.name ?? '')}-${String(states.indexOf(s))}`}
                              value={String(s?.id ?? s?.name ?? '')}
                            >
                              {s.name}
                            </option>
                          ))}
                        </select>
                        {memberErrors.stateId ? (
                          <div className="text-danger">{memberErrors.stateId}</div>
                        ) : null}
                      </div>

                      <div className="col-md-12 col-sm-12 form-group">
                        <div className="field-label">Additional Members / Attendees</div>
                        <div className="mb-3">
                          <button
                            type="button"
                            className="theme-btn btn-style-one"
                            onClick={onAddAttendee}
                          >
                            <span className="btn-title">Add Member</span>
                          </button>
                        </div>

                        {attendees.map((att, idx) => (
                          <div key={idx} className="card mb-3">
                            <div className="card-body">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h5 className="mb-0">Member {idx + 2}</h5>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => onRemoveAttendee(idx)}
                                >
                                  Remove
                                </button>
                              </div>

                              <div className="row">
                                <div className="col-md-6 col-sm-12 form-group">
                                  <div className="field-label">First Name</div>
                                  <input
                                    type="text"
                                    value={att.firstName}
                                    onChange={(e) =>
                                      setAttendees((prev) =>
                                        prev.map((p, i) =>
                                          i === idx ? { ...p, firstName: e.target.value } : p
                                        )
                                      )
                                    }
                                  />
                                  {attendeeErrors[idx]?.firstName ? (
                                    <div className="text-danger">{attendeeErrors[idx].firstName}</div>
                                  ) : null}
                                </div>

                                <div className="col-md-6 col-sm-12 form-group">
                                  <div className="field-label">Last Name</div>
                                  <input
                                    type="text"
                                    value={att.lastName}
                                    onChange={(e) =>
                                      setAttendees((prev) =>
                                        prev.map((p, i) =>
                                          i === idx ? { ...p, lastName: e.target.value } : p
                                        )
                                      )
                                    }
                                  />
                                  {attendeeErrors[idx]?.lastName ? (
                                    <div className="text-danger">{attendeeErrors[idx].lastName}</div>
                                  ) : null}
                                </div>

                                <div className="col-md-6 col-sm-12 form-group">
                                  <div className="field-label">Email</div>
                                  <input
                                    type="email"
                                    value={att.email}
                                    onChange={(e) =>
                                      setAttendees((prev) =>
                                        prev.map((p, i) =>
                                          i === idx ? { ...p, email: e.target.value } : p
                                        )
                                      )
                                    }
                                  />
                                  {attendeeErrors[idx]?.email ? (
                                    <div className="text-danger">{attendeeErrors[idx].email}</div>
                                  ) : null}
                                </div>

                                <div className="col-md-6 col-sm-12 form-group">
                                  <div className="field-label">Phone</div>
                                  <input
                                    type="text"
                                    value={att.phone}
                                    onChange={(e) =>
                                      setAttendees((prev) =>
                                        prev.map((p, i) =>
                                          i === idx ? { ...p, phone: e.target.value } : p
                                        )
                                      )
                                    }
                                  />
                                  {attendeeErrors[idx]?.phone ? (
                                    <div className="text-danger">{attendeeErrors[idx].phone}</div>
                                  ) : null}
                                </div>

                                <div className="col-md-6 col-sm-12 form-group">
                                  <div className="field-label">Relation</div>
                                  <input
                                    type="text"
                                    value={att.relation}
                                    onChange={(e) =>
                                      setAttendees((prev) =>
                                        prev.map((p, i) =>
                                          i === idx ? { ...p, relation: e.target.value } : p
                                        )
                                      )
                                    }
                                  />
                                  {attendeeErrors[idx]?.relation ? (
                                    <div className="text-danger">{attendeeErrors[idx].relation}</div>
                                  ) : null}
                                </div>

                                <div className="col-md-6 col-sm-12 form-group">
                                  <div className="field-label">Address</div>
                                  <input
                                    type="text"
                                    value={att.address}
                                    onChange={(e) =>
                                      setAttendees((prev) =>
                                        prev.map((p, i) =>
                                          i === idx ? { ...p, address: e.target.value } : p
                                        )
                                      )
                                    }
                                  />
                                  {attendeeErrors[idx]?.address ? (
                                    <div className="text-danger">{attendeeErrors[idx].address}</div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="col-md-6 col-sm-12 form-group">
                        <div className="field-label">Monthly Amount</div>
                        <input type="text" value={`$${amount}`} readOnly />
                      </div>

                      <div className="col-md-12 col-sm-12 form-group">
                        <button
                          className="theme-btn btn-style-one"
                          type="submit"
                          disabled={submitting || !isValid}
                        >
                          <span className="btn-title">Submit & Pay</span>
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            <div className="image-column col-xl-4 col-lg-12 col-md-12 col-sm-12">
              <div className="inner">
                <div className="info-block bg-blue">
                  <div className="inner">
                    <h3 style={{ color: '#fff' }}>Summary</h3>
                    <ul className="info" style={{ color: '#fff' }}>
                      <li>Total Members: {totalMembers}</li>
                      <li>Amount: ${amount} / month</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
